import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useWaitlist } from "@clerk/react";
import { motion, AnimatePresence } from "motion/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import logoLight from "@/assets/png/logo.png";
import logoDark from "@/assets/png/logo_reversed.png";

interface WaitlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WaitlistModal({ open, onOpenChange }: WaitlistModalProps) {
  const { waitlist, fetchStatus } = useWaitlist();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const loading = fetchStatus === "fetching";
  const joined = !!waitlist?.id;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await waitlist?.join({ emailAddress: email });
    } catch (err) {
      const e = err as { errors?: { longMessage?: string }[] };
      setError(e.errors?.[0]?.longMessage ?? "Something went wrong. Please try again.");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setEmail("");
      setError("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px] bg-background border-border p-0">
        <VisuallyHidden>
          <DialogTitle>Join the Rayloop waitlist</DialogTitle>
        </VisuallyHidden>

        <div className="flex flex-col gap-6 p-8">
          <div className="flex items-center gap-2">
            <img src={logoLight} alt="Rayloop" className="h-8 dark:hidden" />
            <img src={logoDark} alt="Rayloop" className="hidden h-8 dark:block" />
          </div>

          <AnimatePresence mode="wait">
            {joined ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="space-y-3"
              >
                <DialogHeader className="space-y-1">
                  <h2 className="font-display text-3xl font-bold">You're on the list.</h2>
                  <p className="text-muted-foreground text-base">
                    We'll reach out to{" "}
                    <span className="text-foreground font-medium">{email}</span>{" "}
                    when your spot is ready.
                  </p>
                </DialogHeader>
                <p className="text-muted-foreground text-center text-xs pt-2">
                  Already have access?{" "}
                  <button
                    onClick={() => handleOpenChange(false)}
                    className="underline hover:text-foreground"
                  >
                    <Link to="/login">Log in</Link>
                  </button>
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col gap-6"
              >
                <DialogHeader className="space-y-1">
                  <h2 className="font-display text-3xl font-bold">Start Looping.</h2>
                  <p className="text-muted-foreground text-xl">Request early access</p>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="waitlist-email">Email</Label>
                    <Input
                      id="waitlist-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="bg-transparent"
                    />
                  </div>

                  {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-foreground text-background hover:bg-foreground/90 cursor-pointer"
                    disabled={loading}
                  >
                    {loading ? "Joining..." : "Join the waitlist"}
                  </Button>
                </form>

                <p className="text-muted-foreground text-center text-xs">
                  By continuing, you agree to the{" "}
                  <Link to="/terms" className="underline hover:text-foreground" onClick={() => handleOpenChange(false)}>
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline hover:text-foreground" onClick={() => handleOpenChange(false)}>
                    Privacy Policy
                  </Link>
                  .
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
